import {
  FunctionComponent,
  PropsWithChildren,
  createContext,
  useContext,
  useState,
} from 'react';
import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';
import CheckIcon from '@heroicons/react/24/outline/CheckIcon';
import InfoIcon from '@heroicons/react/24/outline/ChatBubbleOvalLeftIcon';
import Button from 'components/button';
import IconButton from 'components/icon-button';

interface Notification {
  type: 'success' | 'error' | 'information';
  title?: string | null;
  text: string | null;
}

export const NotificationsContext = createContext<{
  notify: (val: Notification) => void;
}>({} as any);

export const useNotifications = () => useContext(NotificationsContext);

interface NotificationsProps extends PropsWithChildren {}

const NotificationsProvider: FunctionComponent<NotificationsProps> = ({
  children,
}) => {
  const [notification, setNotification] = useState<Notification | undefined>();

  const getNotificationTypeHeader = () => {
    switch (notification?.type) {
      case 'success':
        return (
          <div className='w-16 h-16 rounded-full border-2 flex items-center justify-center border-green-500/20'>
            <CheckIcon className='text-green-500 w-7' strokeWidth={3} />
          </div>
        );
      case 'error':
        return (
          <div className='w-16 h-16 rounded-full border-2 flex items-center justify-center border-red-500/20'>
            <XMarkIcon className='text-red-500 w-7' strokeWidth={3} />
          </div>
        );
      case 'information':
        return (
          <div className='w-16 h-16 rounded-full border-2 flex items-center justify-center border-cyan-500/20'>
            <InfoIcon className='text-cyan-500 w-7' strokeWidth={3} />
          </div>
        );
      default:
        return (
          <div className='w-16 h-16 rounded-full border-2 flex items-center justify-center border-cyan-500/20'>
            <InfoIcon className='text-cyan-500 w-7' strokeWidth={3} />
          </div>
        );
    }
  };

  return (
    <NotificationsContext.Provider value={{ notify: setNotification }}>
      {!notification ? null : (
        <div className='fixed animate-in fade-in-0 duration-300 flex z-[1000] bg-black/40 inset-0 p-6'>
          <div className='m-auto bg-white max-w-[500px] w-full rounded-xl p-9 relative animate-in fade-in-25 slide-in-from-bottom-8 duration-300'>
            <IconButton
              customPadding='p-2'
              className='absolute top-2 right-2'
              onClick={() => setNotification(undefined)}
            >
              <XMarkIcon className='w-6' />
            </IconButton>
            <div className='flex justify-center mb-10'>
              {getNotificationTypeHeader()}
            </div>
            <div className='mb-3 text-heading-sm font-bold text-center font-ubuntu'>
              {notification?.title}
            </div>
            <div className='mb-12 text-center'>{notification?.text}</div>
            <div className='flex justify-center'>
              <Button
                className='px-9'
                onClick={() => setNotification(undefined)}
              >
                Uždaryti
              </Button>
            </div>
          </div>
        </div>
      )}
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsProvider;
